import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileText, Loader2, Send, Share2, Sparkles, X } from "lucide-react";
import markdownit from "markdown-it";
import { Button } from "@/components/ui/button";
import { ContentCard } from "@/components/ui/ContentCard";
import { Textarea } from "@/components/ui/textarea";
import AIleafGold from "@/components/ui/AIleafGold.svg";
import { cn } from "@/lib/utils";
import {
  type TeacherScenario,
  type TeacherScenarioMode,
  type TeacherScenarioStage,
} from "@/config/teacherScenarios";
import {
  buildComplicationMessage,
  buildLocalOpeningMessage,
  buildModeSwitchMessage,
  buildScenarioSessionName,
  getScenarioQuickActions,
  hydrateScenarioMessagesFromHistory,
  isResourceRequest,
  isSubstantivePlan,
  parseScenarioSessionFromHistory,
  parseStageAdvancement,
  sendTeacherScenarioMessage,
  stripStageMarkers,
  type ScenarioChatTurn,
  type ScenarioQuickAction,
  type TeacherScenarioSessionContext,
} from "@/services/teacherScenarioService";
import {
  generatePrincipalTakeaway,
  saveScenarioTakeaway,
} from "@/services/teacherScenarioExportService";
import { chatHistoryService } from "@/services/chatHistoryService";
import { sessionService } from "@/services/sessionService";
import { deriveWorkspacePersona } from "@/config/workspacePersona";
import { useAuth } from "@/hooks/useAuth";
import { ScenarioStageTracker } from "./ScenarioStageTracker";
import { ScenarioBriefCard } from "./ScenarioBriefCard";
import { ScenarioTakeawayPreview } from "./ScenarioTakeawayPreview";
import { toast } from "sonner";
import { SCENARIO_PAGE_SHELL } from "./scenarioPageLayout";
import { formatTeacherScenarioSessionError } from "@/config/teacherScenarioSessionSetup";
import "@/components/pages/ai-mentor/components/html-default-styles.css";

const md = markdownit({ html: true, linkify: true, typographer: true });

interface ChatMessage extends ScenarioChatTurn {
  id: string;
  timestamp: Date;
}

interface ScenarioChatPanelProps {
  scenario: TeacherScenario;
  initialMode: TeacherScenarioMode;
  onBackToConfigure: () => void;
  /** When set, reopens an existing coaching session from History instead of starting fresh. */
  resumeSessionId?: string;
  /** Reports the active backend session id so the page can close it when leaving session phase. */
  onSessionActive?: (sessionId: string | null) => void;
}

const ScenarioChatRow = forwardRef<
  HTMLDivElement,
  { msg: ChatMessage; userInitial: string }
>(function ScenarioChatRow({ msg, userInitial }, ref) {
  const isUser = msg.role === "user";
  return (
    <div
      ref={ref}
      className={cn("flex scroll-mt-3 items-start space-x-3", isUser && "flex-row-reverse space-x-reverse")}
    >
      <div
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full md:h-8 md:w-8",
          isUser ? "bg-bodhi-teal" : "bg-white",
        )}
      >
        {isUser ? (
          <span className="text-xs font-medium text-white md:text-sm">{userInitial}</span>
        ) : (
          <img src={AIleafGold} alt="" width={24} height={24} className="h-6 w-6 md:h-8 md:w-8" />
        )}
      </div>
      <div className="relative min-w-0 flex-1">
        <div
          className={cn(
            "rounded-2xl p-3 py-2 text-sm md:p-4 md:text-base",
            isUser
              ? "ml-auto w-fit max-w-[88%] sm:max-w-[72%] rounded-tr-sm bg-bodhi-teal text-white"
              : "w-full max-w-[96%] md:max-w-[94%] rounded-tl-sm bg-gray-100 text-gray-700",
          )}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{msg.content}</p>
          ) : (
            <div className="chat-message-content" dangerouslySetInnerHTML={{ __html: msg.content }} />
          )}
          <time className="mt-2 block text-xs opacity-70">
            {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </time>
        </div>
      </div>
    </div>
  );
});

ScenarioChatRow.displayName = "ScenarioChatRow";

const LOCAL_WELCOME_ID = "welcome-local";

const initialReached = (): Record<TeacherScenarioStage, boolean> => ({
  setup: true,
  plan: true,
  live: false,
  debrief: false,
});

export const ScenarioChatPanel = ({
  scenario,
  initialMode,
  onBackToConfigure,
  resumeSessionId,
  onSessionActive,
}: ScenarioChatPanelProps) => {
  const { userData } = useAuth();
  const persona = deriveWorkspacePersona(userData);
  const userId = userData?.user_id;
  const userInitial = userData?.user_name?.trim()?.[0]?.toUpperCase() ?? "Y";

  const [mode, setMode] = useState<TeacherScenarioMode>(initialMode);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingTakeaway, setIsGeneratingTakeaway] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStage, setActiveStage] = useState<TeacherScenarioStage>("plan");
  const [reachedStages, setReachedStages] = useState(initialReached);
  const [liveTurnCount, setLiveTurnCount] = useState(0);
  const [complicationIndex, setComplicationIndex] = useState(0);
  const [complicationSent, setComplicationSent] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [briefCollapsed, setBriefCollapsed] = useState(true);
  const [takeawayMarkdown, setTakeawayMarkdown] = useState<string | null>(null);
  const [takeawayPreviewOpen, setTakeawayPreviewOpen] = useState(false);
  const [growthDismissed, setGrowthDismissed] = useState(false);
  const [resourceOfferVisible, setResourceOfferVisible] = useState(false);
  const [isSwitchingMode, setIsSwitchingMode] = useState(false);

  const sessionRef = useRef<Awaited<ReturnType<typeof sessionService.getOrCreateChatSession>> | null>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const loadingRowRef = useRef<HTMLDivElement>(null);
  const resourcePanelRef = useRef<HTMLDivElement>(null);
  const userMessageCountRef = useRef(0);

  const ctx = useMemo(
    (): TeacherScenarioSessionContext => ({
      scenarioId: scenario.id,
      mode,
      stage: activeStage,
      complicationIndex,
    }),
    [scenario.id, mode, activeStage, complicationIndex],
  );

  const userMessageCount = useMemo(() => messages.filter((m) => m.role === "user").length, [messages]);

  useEffect(() => {
    userMessageCountRef.current = userMessageCount;
  }, [userMessageCount]);

  const lastAssistantText = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m?.role === "assistant") return m.rawMarkdown ?? m.content;
    }
    return undefined;
  }, [messages]);

  const inDebrief = activeStage === "debrief";
  const showResourceOffer = userMessageCount >= 2 && (inDebrief || resourceOfferVisible);
  const showTakeawayPanel = showResourceOffer && !growthDismissed;
  const showTakeawayReopen = showResourceOffer && growthDismissed;
  const showDebriefHint = activeStage === "debrief" && userMessageCount < 2 && !showTakeawayPanel;

  const markStage = useCallback((stage: TeacherScenarioStage) => {
    setActiveStage(stage);
    setReachedStages((prev) => ({ ...prev, [stage]: true }));
  }, []);

  const appendAssistant = useCallback((rawMarkdown: string, id?: string) => {
    const cleaned = stripStageMarkers(rawMarkdown);
    const html = md.render(cleaned || "No response received.");
    setMessages((prev) => [
      ...prev,
      {
        id: id ?? `a-${Date.now()}`,
        role: "assistant",
        content: html,
        rawMarkdown: cleaned,
        timestamp: new Date(),
      },
    ]);
    return cleaned;
  }, []);

  const runAssistantTurn = useCallback(
    async (
      outgoing: string,
      showAsUser: boolean,
      visibleLabel?: string,
      stageOverride?: TeacherScenarioStage,
    ) => {
      const session = sessionRef.current;
      if (!session) throw new Error("Session not ready.");

      if (showAsUser) {
        setBriefCollapsed(true);
        setMessages((prev) => [
          ...prev,
          {
            id: `u-${Date.now()}`,
            role: "user",
            content: visibleLabel ?? outgoing,
            timestamp: new Date(),
          },
        ]);
      }

      setIsLoading(true);
      setError(null);

      const effectiveCtx = stageOverride ? { ...ctx, stage: stageOverride } : ctx;

      try {
        const response = await sendTeacherScenarioMessage({
          message: outgoing,
          session,
          workspacePersona: persona,
          scenario,
          ctx: effectiveCtx,
        });

        const raw =
          response.content?.body?.replace(/<[^>]+>/g, " ") ??
          response.agent_response ??
          response.content?.plain_text ??
          "";
        const citations = response.content?.citations
          ?.filter((citation) => citation.title)
          .map((citation) => formatOrganizationGuidanceCitation(citation.title ?? "", citation.locator))
          .join("; ");

        const cleaned = appendAssistant(citations ? `${raw}\n\nOrganization guidance: ${citations}` : raw);
        const markerStage = parseStageAdvancement(raw);

        if (markerStage === "live" && mode === "practice") {
          markStage("live");
        } else if (markerStage === "debrief") {
          markStage("debrief");
          setResourceOfferVisible(true);
        }

        return cleaned;
      } finally {
        setIsLoading(false);
      }
    },
    [appendAssistant, ctx, markStage, mode, persona, scenario],
  );

  const handleStageSelect = useCallback(
    (stage: TeacherScenarioStage) => {
      if (stage === "setup") {
        onBackToConfigure();
        return;
      }
      if (stage === "debrief") {
        markStage("debrief");
        if (userMessageCount >= 2) {
          setResourceOfferVisible(true);
          setGrowthDismissed(false);
          requestAnimationFrame(() => {
            resourcePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
          });
        }
        return;
      }
      setActiveStage(stage);
    },
    [markStage, onBackToConfigure, userMessageCount],
  );

  const buildWelcomeMessage = useCallback((): ChatMessage => {
    const localMarkdown = buildLocalOpeningMessage(scenario, initialMode);
    return {
      id: LOCAL_WELCOME_ID,
      role: "assistant",
      content: md.render(localMarkdown),
      rawMarkdown: localMarkdown,
      timestamp: new Date(),
    };
  }, [initialMode, scenario]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setSessionReady(false);
      setError(null);
      setInput("");
      setBriefCollapsed(true);
      setGrowthDismissed(false);
      setResourceOfferVisible(false);
      setLiveTurnCount(0);
      setComplicationIndex(0);
      setComplicationSent(false);
      setIsSwitchingMode(false);
      sessionRef.current = null;

      try {
        if (resumeSessionId) {
          const session = await sessionService.reopenSession(resumeSessionId);
          const history = await chatHistoryService.getChatHistoryBySessionId(resumeSessionId);
          const parsed = parseScenarioSessionFromHistory(history);
          const hydrated = hydrateScenarioMessagesFromHistory(history);

          if (cancelled) return;

          sessionRef.current = session;
          onSessionActive?.(session.id);

          if (parsed) {
            setMode(parsed.mode);
            setActiveStage(parsed.stage);
            setReachedStages(parsed.reachedStages);
            if (parsed.resourceOfferVisible) setResourceOfferVisible(true);
          } else {
            setMode(initialMode);
            setActiveStage("plan");
            setReachedStages(initialReached());
          }

          if (hydrated.length > 0) {
            setMessages(
              hydrated.map((m) => ({
                ...m,
                content:
                  m.role === "assistant" && !m.content.includes("<")
                    ? md.render(m.rawMarkdown ?? m.content)
                    : m.content,
              })),
            );
          } else {
            setMessages([buildWelcomeMessage()]);
          }

          setSessionReady(true);
          return;
        }

        setMode(initialMode);
        setActiveStage("plan");
        setReachedStages(initialReached());
        setMessages([buildWelcomeMessage()]);

        const session = await sessionService.createNewSingleSession("teacher_scenarios");
        if (cancelled) return;

        sessionRef.current = session;
        void sessionService.renameSession(session.id, buildScenarioSessionName(scenario, initialMode));
        onSessionActive?.(session.id);
        setSessionReady(true);
      } catch (err) {
        if (!cancelled) {
          onSessionActive?.(null);
          setError(formatTeacherScenarioSessionError(err));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [buildWelcomeMessage, initialMode, onSessionActive, resumeSessionId, scenario]);

  useEffect(() => {
    const target = isLoading ? loadingRowRef.current : lastMessageRef.current;
    target?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [messages, isLoading]);

  const maybeAdvanceFromPlan = useCallback(
    (userText: string, assistantText: string) => {
      const markerStage = parseStageAdvancement(assistantText);
      // Debrief is only entered when the coach signals it or the teacher chooses
      // to wrap up — never auto-jump mid-conversation.
      if (markerStage === "debrief") {
        markStage("debrief");
        setResourceOfferVisible(true);
        return;
      }
      if (mode === "prep") return;

      if (markerStage === "live" || (isSubstantivePlan(userText) && messages.filter((m) => m.role === "user").length >= 1)) {
        markStage("live");
      }
    },
    [markStage, mode, messages],
  );

  const maybeInjectComplication = useCallback(async () => {
    if (mode !== "practice" || activeStage !== "live" || complicationSent) return;
    const threshold = scenario.complicationAfterTurns ?? 2;
    if (liveTurnCount < threshold) return;
    if (complicationIndex >= scenario.complications.length) return;

    setComplicationSent(true);
    try {
      await runAssistantTurn(buildComplicationMessage(scenario, complicationIndex), false);
      setComplicationIndex((i) => i + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add complication.");
    }
  }, [
    activeStage,
    complicationIndex,
    complicationSent,
    liveTurnCount,
    mode,
    runAssistantTurn,
    scenario,
  ]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput("");

    const wantsResource = isResourceRequest(text);
    if (wantsResource) {
      markStage("debrief");
      setResourceOfferVisible(true);
    }

    try {
      const assistantText = await runAssistantTurn(
        text,
        true,
        undefined,
        wantsResource ? "debrief" : undefined,
      );
      maybeAdvanceFromPlan(text, assistantText ?? "");

      if (activeStage === "live" || reachedStages.live) {
        setLiveTurnCount((c) => c + 1);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send message.");
    }
  };

  const handleSwitchMode = useCallback(async () => {
    if (isLoading || isSwitchingMode || !sessionReady) return;
    const next: TeacherScenarioMode = mode === "prep" ? "practice" : "prep";
    setIsSwitchingMode(true);
    setMode(next);

    let nextStage: TeacherScenarioStage = activeStage;
    if (next === "practice" && activeStage === "plan") {
      nextStage = "live";
      markStage("live");
    } else if (next === "prep" && activeStage === "live") {
      nextStage = "plan";
      setActiveStage("plan");
    }

    try {
      await runAssistantTurn(buildModeSwitchMessage(scenario, next, nextStage), false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to switch mode.");
    } finally {
      setIsSwitchingMode(false);
    }
  }, [activeStage, isLoading, isSwitchingMode, markStage, mode, runAssistantTurn, scenario, sessionReady]);

  const handleGenerateTakeaway = async () => {
    const session = sessionRef.current;
    if (!session || !userId) {
      toast.error("Sign in to generate a takeaway document.");
      return;
    }

    setIsGeneratingTakeaway(true);
    try {
      const markdown = await generatePrincipalTakeaway({
        scenario,
        mode,
        messages,
        session,
        workspacePersona: persona,
      });
      saveScenarioTakeaway(userId, {
        scenarioId: scenario.id,
        scenarioTitle: scenario.title,
        mode,
        markdown,
      });
      setTakeawayMarkdown(markdown);
      setTakeawayPreviewOpen(true);
      toast.success("Takeaway saved to History.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not generate takeaway.");
    } finally {
      setIsGeneratingTakeaway(false);
    }
  };

  useEffect(() => {
    void maybeInjectComplication();
  }, [liveTurnCount, maybeInjectComplication]);

  const stageChips = useMemo(
    () => getScenarioQuickActions(activeStage, mode, lastAssistantText),
    [activeStage, mode, lastAssistantText],
  );

  const handleQuickAction = useCallback(
    async (action: ScenarioQuickAction) => {
      if (isLoading || !sessionReady) return;

      if (action.offerResource && !action.prompt.trim()) {
        markStage("debrief");
        setResourceOfferVisible(true);
        return;
      }

      const nextStage = action.advanceTo ?? (action.offerResource ? "debrief" : undefined);
      if (nextStage) markStage(nextStage);
      if (action.offerResource || nextStage === "debrief") setResourceOfferVisible(true);

      try {
        await runAssistantTurn(action.prompt, true, undefined, nextStage);
        if (activeStage === "live" || reachedStages.live) {
          setLiveTurnCount((c) => c + 1);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to send message.");
      }
    },
    [activeStage, isLoading, markStage, reachedStages.live, runAssistantTurn, sessionReady],
  );

  const inputDisabled = isLoading || !sessionReady;
  const inputPlaceholder = !sessionReady
    ? "Starting your session…"
    : activeStage === "plan"
      ? "Describe how you'd handle this…"
      : "Continue the conversation…";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      <ScenarioBriefCard
        scenario={scenario}
        mode={mode}
        collapsed={briefCollapsed}
        onCollapsedChange={setBriefCollapsed}
      />

      <ScenarioStageTracker
        mode={mode}
        activeStage={activeStage}
        reachedStages={reachedStages}
        onStageSelect={handleStageSelect}
        onSwitchMode={() => void handleSwitchMode()}
        switchModeDisabled={isLoading || !sessionReady}
        isSwitchingMode={isSwitchingMode}
      />

      <ContentCard className={cn(SCENARIO_PAGE_SHELL.chatCard, "min-h-[280px]")}>
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 py-4 md:px-5 md:py-6">
          {!sessionReady && messages.length === 0 && (
            <div className="flex items-center gap-2 font-inter text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin text-bodhi-teal" />
              Preparing your coached session…
            </div>
          )}
          {messages.map((msg, idx) => (
            <ScenarioChatRow
              key={msg.id}
              ref={idx === messages.length - 1 && !isLoading ? lastMessageRef : undefined}
              msg={msg}
              userInitial={userInitial}
            />
          ))}
          {isLoading && sessionReady && (
            <div ref={loadingRowRef} className="flex items-center gap-2 font-inter text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin text-bodhi-teal" />
              Coaching…
            </div>
          )}
          </div>

        {error && (
          <div className="border-t border-red-100 bg-red-50 px-4 py-3 font-inter text-sm text-red-800 md:px-6">
            <p className="whitespace-pre-wrap">{error}</p>
          </div>
        )}

        {showDebriefHint && (
          <div className="border-t border-amber-100 bg-amber-50 px-4 py-2 font-inter text-xs text-amber-900 md:px-6">
            Exchange a couple of messages with your coach first — then you can create your saved resource here.
          </div>
        )}

        {showTakeawayPanel && (
          <div
            ref={resourcePanelRef}
            className="border-t border-bodhi-teal/10 bg-bodhi-teal/5 px-4 py-3 md:px-6"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-inter text-sm font-semibold text-bodhi-teal">Create your saved resource</p>
              <button
                type="button"
                aria-label="Dismiss and keep coaching"
                onClick={() => setGrowthDismissed(true)}
                className="-mr-1 rounded-md p-1 text-gray-400 transition hover:bg-white hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ul className="mt-2 space-y-1 font-inter text-xs text-gray-700">
              <li className="flex gap-2">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bodhi-teal" />
                <span><strong>Feel ready:</strong> use the reflection moves above to name your strengths and next step.</span>
              </li>
              <li className="flex gap-2">
                <Share2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bodhi-teal" />
                <span><strong>Share with admin:</strong> generate a professional takeaway you can hand to your principal.</span>
              </li>
              <li className="flex gap-2">
                <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bodhi-teal" />
                <span><strong>Keep for yourself:</strong> the takeaway is saved to your History as a personal resource.</span>
              </li>
            </ul>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={isGeneratingTakeaway || isLoading}
                onClick={() => void handleGenerateTakeaway()}
                className="bg-bodhi-teal font-inter text-white hover:bg-bodhi-teal/90"
              >
                {isGeneratingTakeaway ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="mr-1.5 h-4 w-4" />
                )}
                Create my takeaway
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setGrowthDismissed(true)}
                className="font-inter text-gray-600 hover:text-bodhi-teal"
              >
                Keep coaching
              </Button>
            </div>
          </div>
        )}

        {showTakeawayReopen && (
          <div className="border-t border-bodhi-teal/10 bg-bodhi-teal/5 px-4 py-2 md:px-6">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={isGeneratingTakeaway || isLoading}
              onClick={() => void handleGenerateTakeaway()}
              className="font-inter text-bodhi-teal hover:bg-white"
            >
              <FileText className="mr-1.5 h-4 w-4" />
              Create takeaway when you're ready
            </Button>
          </div>
        )}

        <div className="shrink-0 space-y-3 border-t border-gray-200 bg-gray-50/60 p-4 md:px-6">
          {stageChips.length > 0 && (
            <div className="space-y-1.5">
              <p className="font-inter text-[11px] font-medium uppercase tracking-wide text-gray-400">
                Quick coaching moves
              </p>
              <div className="flex flex-wrap gap-2">
                {stageChips.map((chip) => (
                  <button
                    key={chip.prompt}
                    type="button"
                    disabled={inputDisabled}
                    onClick={() => void handleQuickAction(chip)}
                    className="rounded-full border border-bodhi-teal/25 bg-bodhi-teal/5 px-3 py-1 font-inter text-xs font-medium text-bodhi-teal transition hover:bg-bodhi-teal/10 disabled:opacity-50"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={inputPlaceholder}
              rows={2}
              disabled={inputDisabled}
              className="min-h-[72px] resize-none border-gray-200 bg-white font-inter text-gray-800"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
            />
            <Button
              type="button"
              onClick={() => void handleSend()}
              disabled={inputDisabled || !input.trim()}
              className="shrink-0 self-end bg-bodhi-teal font-inter hover:bg-bodhi-teal/90"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
        </div>
      </ContentCard>

      {takeawayMarkdown && (
        <ScenarioTakeawayPreview
          open={takeawayPreviewOpen}
          onOpenChange={setTakeawayPreviewOpen}
          markdown={takeawayMarkdown}
          scenarioTitle={scenario.title}
          defaultAuthorName={userData?.user_name ?? ""}
        />
      )}
    </div>
  );
};

function formatOrganizationGuidanceCitation(title: string, locator?: string | null): string {
  return [title, locator]
    .filter((value): value is string => Boolean(value))
    .map((value) => md.utils.escapeHtml(value).replace(/([\\`*_{}()[\]#+.!-])/g, "\\$1"))
    .join(" — ");
}
